import { useI18nContext } from "@hopper-b2b/i18n";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import {
  NoticeBanner as NoticeBannerUI,
  type NoticePriority,
} from "@/overrides/apac/apac-ui";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";
import { useIsCheckout } from "@/utilities/hooks/useIsCheckout";

export interface NoticeLanguageContent {
  description?: string;
  title: string;
}

export interface Notice {
  en: NoticeLanguageContent;
  fr: NoticeLanguageContent;
  priority: NoticePriority;
  enabled: boolean;
  funnel?: string;
  checkout?: boolean;
}

export interface NoticesContainer {
  notices: Notice[];
}

export const NoticeBanner = () => {
  const { getExperimentJsonPayload } = useExperiment();
  const noticeContainer = getExperimentJsonPayload<NoticesContainer>(
    Experiments.IMPORTANT_NOTICE_BANNER.name
  );

  const currentFunnel = useGetCurrentFunnel();
  const isCheckout = useIsCheckout();
  const { locale } = useI18nContext();

  const notices =
    noticeContainer?.notices?.filter((notice) => {
      // Only show enabled notices
      if (!notice.enabled) return false;

      // If on a checkout page, ONLY show checkout notices (exclusive)
      if (isCheckout) {
        if (!notice.checkout) return false;

        // If funnel is also specified, match both checkout AND funnel
        if (notice.funnel) {
          return notice.funnel === currentFunnel;
        }

        // Checkout=true, no funnel specified -> show on all checkout pages
        return true;
      }

      // Not on checkout page: exclude checkout-only notices
      if (notice.checkout) return false;

      // Non-checkout notices: handle funnel filtering
      if (!notice.funnel) return true; // Global notice
      return notice.funnel === currentFunnel;
    }) ?? [];

  return (
    <>
      {notices.map((notice, index) => {
        const strings = locale.startsWith("fr") ? notice.fr : notice.en;
        if (!strings?.title) {
          return null;
        }
        return (
          <NoticeBannerUI
            key={`notice-${index}-${strings.title}`}
            title={strings.title}
            description={strings.description}
            priority={notice.priority}
          />
        );
      })}
    </>
  );
};
